import { ProgressEventData } from "@/hooks/services/mutations/ai-agent/use-demo-chat-stream-mutation";
import { JobStatusTableData } from "@/types/job-status";
import { create } from "zustand";

export interface Attachment {
  id: number;
  s3Url: string;
  originalName: string;
  filesize: string;
  type: "pdf" | "csv" | "image";
  fileName: string;
  imageUrl: string;
  file?: File; // multipart/form-data 전송을 위한 실제 File 객체
}

export type EventType =
  | "idle"
  | "progress"
  | "token"
  | "final_report"
  | "references"
  | "tokens"
  | "complete"
  | "stopped"
  | "error";

export interface PipelineExpandState {
  isExpanded: boolean;
  isNoteExpanded: boolean;
  expandedPhases: Set<string>;
  expandedTasks: Set<string>;
}

/** 채팅방별 소스/옵션 토글 값 (미설정 시 기본값 사용) */
export interface ChatSourceSettings {
  deepResearch: boolean;
  academicJournalEnabled: boolean;
  webSearchEnabled: boolean;
  privateKbEnabled: boolean;
  biomcpEnabled: boolean;
}

export const DEFAULT_CHAT_SOURCE_SETTINGS: ChatSourceSettings = {
  deepResearch: false,
  academicJournalEnabled: false,
  webSearchEnabled: true,
  privateKbEnabled: false,
  biomcpEnabled: true,
};

export interface AiAgentStore {
  message: string;
  setMessage: (message: string) => void;
  attachments: Attachment[];
  setAttachments: (attachments: Attachment[]) => void;
  removeAttachment: (id: number) => void;
  selectedJob: JobStatusTableData | null;
  setSelectedJob: (selectedJob: JobStatusTableData | null) => void;
  clearSelectedJob: () => void;
  /** 채팅방별 소스 설정 (키: chatId 또는 "temp". 미설정 시 DEFAULT_CHAT_SOURCE_SETTINGS) */
  chatSourceSettingsMap: Map<string, ChatSourceSettings>;
  getChatSourceSettings: (chatId: string) => ChatSourceSettings;
  setChatSourceSetting: <K extends keyof ChatSourceSettings>(
    chatId: string,
    key: K,
    value: ChatSourceSettings[K]
  ) => void;
  migrateChatSourceSettings: (oldChatId: string, newChatId: string) => void;
  eventType: EventType;
  setEventType: (eventType: EventType) => void;
  progressListMap: Map<string, ProgressEventData[]>;
  getProgressList: (chatId: string) => ProgressEventData[];
  setProgressList: (chatId: string, progressList: ProgressEventData[]) => void;
  addProgress: (chatId: string, progress: ProgressEventData) => void;
  clearProgressList: (chatId: string) => void;
  migrateProgressList: (oldChatId: string, newChatId: string) => void;
  streamingChatIds: Set<string>;
  addStreamingChatId: (chatId: string) => void;
  removeStreamingChatId: (chatId: string) => void;
  completedChatIds: Set<string>;
  setStreamComplete: (chatId: string) => void;
  isStreamComplete: (chatId: string) => boolean;
  clearStreamComplete: (chatId: string) => void;
  pipelineExpandStateMap: Map<string, PipelineExpandState>;
  getPipelineExpandState: (chatId: string) => PipelineExpandState;
  setPipelineExpandState: (chatId: string, state: Partial<PipelineExpandState>) => void;
  migratePipelineExpandState: (oldChatId: string, newChatId: string) => void;
  clearPipelineExpandState: (chatId: string) => void;
  showCreditError: boolean;
  setShowCreditError: (show: boolean) => void;
  showFileUploadError: boolean;
  setShowFileUploadError: (show: boolean) => void;
}

export const useAiAgentStore = create<AiAgentStore>((set, get) => ({
  message: "",
  setMessage: (message: string) => set({ message }),
  attachments: [],
  setAttachments: (attachments: Attachment[]) => set({ attachments }),
  removeAttachment: (id: number) =>
    set((state) => ({
      attachments: state.attachments.filter(
        (attachment) => attachment.id !== id
      ),
    })),
  selectedJob: null,
  setSelectedJob: (selectedJob: JobStatusTableData | null) =>
    set({ selectedJob }),
  clearSelectedJob: () => set({ selectedJob: null }),
  eventType: "idle",
  setEventType: (eventType: EventType) => set({ eventType }),
  chatSourceSettingsMap: new Map<string, ChatSourceSettings>(),
  getChatSourceSettings: (chatId: string) => {
    const settings = get().chatSourceSettingsMap.get(chatId);
    return settings ?? { ...DEFAULT_CHAT_SOURCE_SETTINGS };
  },
  setChatSourceSetting: (chatId: string, key: keyof ChatSourceSettings, value: boolean) =>
    set((state) => {
      const newMap = new Map(state.chatSourceSettingsMap);
      const current = newMap.get(chatId) ?? { ...DEFAULT_CHAT_SOURCE_SETTINGS };
      newMap.set(chatId, { ...current, [key]: value });
      return { chatSourceSettingsMap: newMap };
    }),
 migrateChatSourceSettings: (oldChatId: string, newChatId: string) =>
    set((state) => {
      // 동일 키로의 마이그레이션은 의미 없고, delete 과정에서 설정이 사라질 수 있으므로 방어
      if (oldChatId === newChatId) {
        return state;
      }
      // oldChatId(예: "temp-1710000000-abc") 키에서 먼저 찾고,
      // 없으면 "temp" 키에서 fallback (홈→채팅 전환 시 키 불일치 대응)
      const settings =
        state.chatSourceSettingsMap.get(oldChatId) ??
        state.chatSourceSettingsMap.get("temp");
      if (!settings) return state;
      const newMap = new Map(state.chatSourceSettingsMap);
      newMap.set(newChatId, settings);
      newMap.delete(oldChatId);
      newMap.delete("temp"); // "temp" 키도 정리
      return { chatSourceSettingsMap: newMap };
    }),
  progressListMap: new Map<string, ProgressEventData[]>(),
  getProgressList: (chatId: string) => {
    return get().progressListMap.get(chatId) || [];
  },
  setProgressList: (chatId: string, progressList: ProgressEventData[]) =>
    set((state) => {
      const newMap = new Map(state.progressListMap);
      newMap.set(chatId, progressList);
      return { progressListMap: newMap };
    }),
  addProgress: (chatId: string, progress: ProgressEventData) =>
    set((state) => {
      const newMap = new Map(state.progressListMap);
      const currentList = newMap.get(chatId) || [];
      newMap.set(chatId, [...currentList, progress]);
      return { progressListMap: newMap };
    }),
  clearProgressList: (chatId: string) =>
    set((state) => {
      const newMap = new Map(state.progressListMap);
      newMap.delete(chatId);
      return { progressListMap: newMap };
    }),
  migrateProgressList: (oldChatId: string, newChatId: string) =>
    set((state) => {
      const newMap = new Map(state.progressListMap);
      const oldProgress = newMap.get(oldChatId);
      if (oldProgress) {
        newMap.set(newChatId, oldProgress);
        newMap.delete(oldChatId);
      }
      return { progressListMap: newMap };
    }),
  streamingChatIds: new Set<string>(),
  addStreamingChatId: (chatId: string) =>
    set((state) => {
      const newSet = new Set(state.streamingChatIds);
      newSet.add(chatId);
      return { streamingChatIds: newSet };
    }),
  removeStreamingChatId: (chatId: string) =>
    set((state) => {
      const newSet = new Set(state.streamingChatIds);
      newSet.delete(chatId);
      return { streamingChatIds: newSet };
    }),
  completedChatIds: new Set<string>(),
  setStreamComplete: (chatId: string) =>
    set((state) => {
      const newSet = new Set(state.completedChatIds);
      newSet.add(chatId);
      return { completedChatIds: newSet };
    }),
  isStreamComplete: (chatId: string) => {
    return get().completedChatIds.has(chatId);
  },
  clearStreamComplete: (chatId: string) =>
    set((state) => {
      const newSet = new Set(state.completedChatIds);
      newSet.delete(chatId);
      return { completedChatIds: newSet };
    }),
  pipelineExpandStateMap: new Map<string, PipelineExpandState>(),
  getPipelineExpandState: (chatId: string) => {
    const state = get().pipelineExpandStateMap.get(chatId);
    if (state) return state;
    // 기본값 반환
    return {
      isExpanded: false,
      isNoteExpanded: false,
      expandedPhases: new Set<string>(),
      expandedTasks: new Set<string>(),
    };
  },
  setPipelineExpandState: (chatId: string, newState: Partial<PipelineExpandState>) =>
    set((state) => {
      const newMap = new Map(state.pipelineExpandStateMap);
      const currentState = newMap.get(chatId) || {
        isExpanded: false,
        isNoteExpanded: false,
        expandedPhases: new Set<string>(),
        expandedTasks: new Set<string>(),
      };
      // Set 객체를 제대로 병합
      newMap.set(chatId, {
        isExpanded: newState.isExpanded !== undefined ? newState.isExpanded : currentState.isExpanded,
        isNoteExpanded: newState.isNoteExpanded !== undefined ? newState.isNoteExpanded : currentState.isNoteExpanded,
        expandedPhases: newState.expandedPhases !== undefined ? newState.expandedPhases : currentState.expandedPhases,
        expandedTasks: newState.expandedTasks !== undefined ? newState.expandedTasks : currentState.expandedTasks,
      });
      return { pipelineExpandStateMap: newMap };
    }),
  migratePipelineExpandState: (oldChatId: string, newChatId: string) =>
    set((state) => {
      const newMap = new Map(state.pipelineExpandStateMap);
      const oldState = newMap.get(oldChatId);
      if (oldState) {
        newMap.set(newChatId, oldState);
        newMap.delete(oldChatId);
      }
      return { pipelineExpandStateMap: newMap };
    }),
  clearPipelineExpandState: (chatId: string) =>
    set((state) => {
      const newMap = new Map(state.pipelineExpandStateMap);
      newMap.delete(chatId);
      return { pipelineExpandStateMap: newMap };
    }),
  showCreditError: false,
  setShowCreditError: (show: boolean) => set({ showCreditError: show }),
  showFileUploadError: false,
  setShowFileUploadError: (show: boolean) => set({ showFileUploadError: show }),
}));
